import {
  type TargetAndTransition,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { isEmpty } from 'lodash-es';
import {
  type AnimationEventHandler,
  forwardRef,
  useEffect,
  useRef,
  type HTMLAttributes,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';

import { usePanelsContext } from './hooks/_usePanelsContext.js';
import { panelBaseCSS } from './Panel.sty.js';
import { PanelsActions } from './types/panels-state.js';
import type {
  DismissOptions,
  PanelSize,
  PanelSizes,
} from './types/shared-panel-types.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';

/** @internal */
export interface PanelProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /** PanelName */
  name: string;
  /** The preferred width of the panel in px or %. */
  preferredWidth?: PanelSize;
  /** The minimum width the panel should have in pixels. */
  minWidth?: number;
  /** If the panel should be resizable. */
  resizable?: boolean;
  /**
   * If the panel should be dismissible.
   * @defaultValue 'left'
   */
  dismissible?: DismissOptions;
  /**
   * The order of the panels.
   * Needed for sorting the panels to ensure correct resizing behaviour.
   * If e.g. the page is not unmounted when navigating between routes, the 'main'
   * panel might be added before the 'sidebar' panel.
   * With the 'order' prop, we can still sort them correctly.
   */
  order: number;
  /**
   * Callback triggered after resizing is finished with the new panel sizes of
   * all existing panels and the newly applied panel width (in px).
   */
  onResize?: (sizes: PanelSizes, width: number) => void;
}

/**
 * The `Panel` component adds a panel to the `ParentPanel`. The panel can be rendered
 * conditionally and can have a variety of configurations to e.g. give it a preferred width.
 * @internal
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(
  (props, forwardedRef) => {
    const {
      name,
      preferredWidth,
      children,
      minWidth,
      resizable = true,
      dismissible: dismissibleProp = 'left',
      order,
      onResize,
      className: consumerClassName,
      style: consumerStyle,
      // Destructure animation callbacks from React.HTMLAttributes<HTMLDivElement>.
      // Those would conflict with the framer motion callbacks.
      // Should be fixed by the type once we have a concept for how to allow custom styles.
      onAnimationStart,
      onDrag,
      onDragEnd,
      onDragStart,
      'data-testid': dataTestId,
      role,
      tabIndex = 0,
      ...remainingProps
    }: PanelProps = props;

    const panelRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useMergeRefs([panelRef, forwardedRef]);
    const { disableAnimations, panelSizes, dispatch } = usePanelsContext();
    const dismissible = dismissibleProp === true ? 'left' : dismissibleProp;

    const prefersReducedMotion = useReducedMotion();
    const animationProps: {
      [key: string]: TargetAndTransition | AnimationEventHandler;
    } = {
      initial: {
        position: 'absolute',
        ...(dismissible === 'left' ? { left: 0 } : { right: 0 }),
        opacity: 1,
      },
      animate: {
        position: 'relative',
        opacity: 1,
        transition: {
          delay: AnimationsJS.SlideIn.Duration.Fast,
        },
      },
      onAnimationStart: () => {
        // Adding the width to the animation would sometimes result in the content
        // flickering from full width to the final width when the animation is done.
        // Have to set it on the DOM node directly.
        // This is to make it appear like the opening panel is revealing the already
        // rendered content underneath, without the content reflowing.
        setTimeout(() => {
          panelRef.current &&
            (panelRef.current.style.width =
              panelSizes &&
              !isEmpty(panelSizes.current) &&
              panelSizes.current[name] !== undefined
                ? `${panelSizes.current[name].px}px`
                : '100%');
        });
      },
      onAnimationComplete: () => {
        panelRef.current && (panelRef.current.style.width = '100%');
        window.dispatchEvent(new Event('animationDone'));
      },
    };

    useEffect(() => {
      dispatch({
        type: PanelsActions.ADDPANEL,
        payload: {
          name,
          order,
          preferredWidth,
          minWidth,
          resizable,
          dismissible,
          onResize,
        },
      });

      return () => {
        panelRef.current && (panelRef.current.style.width = '100%');
        dispatch({
          type: PanelsActions.REMOVEPANEL,
          payload: {
            name,
          },
        });
      };
      // only for mount and unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      dispatch({
        type: PanelsActions.UPDATEPANEL,
        payload: {
          name,
          preferredWidth,
          minWidth,
        },
      });
    }, [dispatch, name, preferredWidth, minWidth]);

    const disabledAnimations = prefersReducedMotion || disableAnimations;

    return (
      <div
        style={{
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'auto',
          gridArea: name,
          gridRow: '2',
          ...consumerStyle,
        }}
        data-testid={dataTestId}
        className={consumerClassName}
        {...remainingProps}
      >
        <motion.div
          data-testid={`page-panel-${name}`}
          ref={rootRef}
          {...(!disabledAnimations && animationProps)}
          tabIndex={tabIndex}
          role={role}
          className={panelBaseCSS}
        >
          {children}
        </motion.div>
      </div>
    );
  },
);
